import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import IdeaCardContent from './IdeaCardContent';
import { Idea } from '../../interfaces/Idea';
import { getYouTubeEmbedUrl } from '../../utils/youtube';
import { linkifyHtml } from '../../utils/linkifyHtml';
import { dotvotingApi } from '../../services/dotvotingApi';

interface UrlPreview {
  title: string;
  image: string;
}

const previewCache = new Map<string, Promise<UrlPreview | null>>();

const getCachedUrlPreview = (topicId: string, url: string): Promise<UrlPreview | null> => {
  const cacheKey = `${topicId}::${url}`;
  const cachedPreview = previewCache.get(cacheKey);
  if (cachedPreview) {
    return cachedPreview;
  }

  const previewPromise = dotvotingApi
    .getUrlPreview(topicId, url)
    .then((preview) => {
      if (!preview?.title || !preview?.image) {
        return null;
      }

      return {
        title: preview.title,
        image: preview.image,
      };
    })
    .catch(() => null);

  previewCache.set(cacheKey, previewPromise);
  return previewPromise;
};

const extractAnchorHrefs = (html: string): string[] => {
  if (!html || typeof DOMParser === 'undefined') {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const hrefs = Array.from(doc.querySelectorAll('a[href]'))
    .map((anchor) => anchor.getAttribute('href')?.trim() ?? '')
    .filter(Boolean);

  return Array.from(new Set(hrefs));
};

const replaceLinksWithPreviews = (
  html: string,
  previewsByUrl: Record<string, UrlPreview | undefined>
): string => {
  if (!html || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const anchors = Array.from(doc.querySelectorAll('a[href]'));

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (!href) {
      return;
    }

    const preview = previewsByUrl[href];
    if (!preview) {
      return;
    }

    const previewLink = doc.createElement('a');
    previewLink.href = href;
    previewLink.target = '_blank';
    previewLink.rel = 'noopener noreferrer';
    previewLink.className = 'idea-link-preview';

    const previewImage = doc.createElement('img');
    previewImage.src = preview.image;
    previewImage.alt = preview.title;
    previewImage.loading = 'lazy';
    previewImage.className = 'idea-link-preview__image';

    const previewTitle = doc.createElement('span');
    previewTitle.className = 'idea-link-preview__title';
    previewTitle.textContent = preview.title;

    previewLink.append(previewImage, previewTitle);
    anchor.replaceWith(previewLink);
  });

  return doc.body.innerHTML;
};

const replaceEmbeddedVideoLinks = (html: string): string => {
  if (!html || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const anchors = Array.from(doc.querySelectorAll('a[href]'));

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    const embedUrl = href ? getYouTubeEmbedUrl(href) : null;
    if (!embedUrl) {
      return;
    }

    const videoWrapper = doc.createElement('div');
    videoWrapper.className = 'idea-video-embed';

    const iframe = doc.createElement('iframe');
    iframe.src = embedUrl;
    iframe.title = 'YouTube video';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.className = 'idea-video-embed__frame';

    videoWrapper.append(iframe);
    anchor.replaceWith(videoWrapper);
  });

  return doc.body.innerHTML;
};

const hasRenderableHtmlContent = (html: string): boolean =>
  /<(img|iframe)\b[^>]*>/i.test(html) ||
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

export interface TextIdeaCardProps {
  showBackground: boolean;
  idea: Idea;
  size?: 'default' | 'large';
}

const TextIdeaCard = ({ showBackground, idea, size = 'default' }: TextIdeaCardProps) => {
  const displayContent =
    idea.type === 'file'
      ? idea.title?.trim() || idea.content?.trim() || ''
      : idea.content;
  const youtubeEmbedUrl = getYouTubeEmbedUrl(displayContent);
  const linkifiedContent = linkifyHtml(displayContent);
  const [previewsByUrl, setPreviewsByUrl] = useState<Record<string, UrlPreview | undefined>>({});
  const linkedUrls = useMemo(() => extractAnchorHrefs(linkifiedContent), [linkifiedContent]);
  const renderedContent = useMemo(
    () => replaceLinksWithPreviews(linkifiedContent, previewsByUrl),
    [linkifiedContent, previewsByUrl]
  );
  const contentWithEmbeddedVideo = useMemo(
    () => replaceEmbeddedVideoLinks(renderedContent),
    [renderedContent]
  );
  const hasTextContent = useMemo(
    () => hasRenderableHtmlContent(contentWithEmbeddedVideo),
    [contentWithEmbeddedVideo]
  );

  useEffect(() => {
    if (youtubeEmbedUrl || linkedUrls.length === 0) {
      setPreviewsByUrl({});
      return;
    }

    let isCancelled = false;

    void Promise.all(
      linkedUrls.map(async (url) => {
        const preview = await getCachedUrlPreview(idea.topic, url);
        return { url, preview };
      })
    ).then((results) => {
      if (isCancelled) {
        return;
      }

      const nextState: Record<string, UrlPreview | undefined> = {};
      results.forEach(({ url, preview }) => {
        if (preview) {
          nextState[url] = preview;
        }
      });
      setPreviewsByUrl(nextState);
    });

    return () => {
      isCancelled = true;
    };
  }, [idea.topic, linkedUrls, youtubeEmbedUrl]);

  const handleContentClickCapture = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (link) {
      event.stopPropagation();
    }
  };

  return (
    <IdeaCardContent showBackground={showBackground} showPadding={true}>
      {hasTextContent && (
        <Box
          component="div"
          onClickCapture={handleContentClickCapture}
          sx={
            size === 'large'
              ? {
                  fontSize: 18,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  '& img': { width: '100%' },
                  '& a': {
                    color: '#04ceff',
                    fontWeight: 500,
                    pointerEvents: 'auto',
                    textDecoration: 'none',
                  },
                  '& a:not(.idea-link-preview):hover': {
                    textDecoration: 'underline',
                  },
                  '& .idea-video-embed': { my: 1.5 },
                  '& .idea-video-embed__frame': {
                    width: '100%',
                    aspectRatio: '16 / 9',
                    border: 0,
                    borderRadius: 1,
                    display: 'block',
                  },
                  '& .idea-link-preview': {
                    display: 'block',
                    textDecoration: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    my: 0.5,
                  },
                  '& .idea-link-preview__image': {
                    display: 'block',
                    width: '100%',
                    maxHeight: 220,
                    objectFit: 'cover',
                  },
                  '& .idea-link-preview__title': {
                    display: 'block',
                    px: 1,
                    py: 0.75,
                    color: 'text.primary',
                    fontWeight: 600,
                    lineHeight: 1.3,
                  },
                }
              : {
                  fontSize: 16,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  '& img': { width: '100%' },
                  '& a': {
                    color: '#04ceff',
                    fontWeight: 600,
                    pointerEvents: 'auto',
                    textDecoration: 'none',
                  },
                  '& a:not(.idea-link-preview):hover': {
                    textDecoration: 'underline',
                  },
                  '& .idea-video-embed': { my: 1.5 },
                  '& .idea-video-embed__frame': {
                    width: '100%',
                    aspectRatio: '16 / 9',
                    border: 0,
                    borderRadius: 1,
                    display: 'block',
                  },
                  '& .idea-link-preview': {
                    display: 'block',
                    textDecoration: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    my: 0.5,
                  },
                  '& .idea-link-preview__image': {
                    display: 'block',
                    width: '100%',
                    maxHeight: 160,
                    objectFit: 'cover',
                  },
                  '& .idea-link-preview__title': {
                    display: 'block',
                    px: 1,
                    py: 0.75,
                    color: 'text.primary',
                    fontWeight: 600,
                    lineHeight: 1.3,
                  },
                }
          }
          dangerouslySetInnerHTML={{
            __html: contentWithEmbeddedVideo,
          }}
        />
      )}
    </IdeaCardContent>
  );
};

export default TextIdeaCard;
